import React from "react";
import { Stack, IconButton, Spinner, Panel, PanelType } from "@fluentui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import rehypeRaw from "rehype-raw";
import { AISearchRAGCitation_Parsed } from "../../api";
import styles from "./Chat.module.css";
import PdfViewerWithPagination from '../../components/Answer/PdfViewerWithPagination';


interface MobileCitationPanelProps {
    activeCitation: AISearchRAGCitation_Parsed | undefined;
    isOpen: boolean;
    onClose: () => void;
    onViewSource: (citation: AISearchRAGCitation_Parsed) => void;
    urlBlobMap: Map<string, string>;
    loadingBlobs: Set<string>;
}


export const MobileCitationPanel: React.FC<MobileCitationPanelProps> = ({
    activeCitation,
    isOpen,
    onClose,
    onViewSource,
    urlBlobMap,
    loadingBlobs
}) => {
    if (!activeCitation) {
        return null;
    }


    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onClose}
            type={PanelType.custom}
            customWidth="100%"
            isLightDismiss={true}
            closeButtonAriaLabel="閉じる"
            hasCloseButton={false}
            styles={{
                root: {
                    top: '0',
                    height: '100%',
                },
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                },
                content: {
                    padding: 0,
                    height: '100%',
                },
                scrollableContent: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Stack
                styles={{
                    root: {
                        height: '100%',
                        backgroundColor: 'white',
                    }
                }}
            >
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    styles={{
                        root: {
                            padding: '16px',
                            borderBottom: '1px solid #e1e1e1',
                            backgroundColor: '#f5f5f5',
                        }
                    }}
                >
                    <Stack.Item>
                        <span
                            style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#323130',
                            }}
                        >
                            引用元{activeCitation.page ? `（${activeCitation.page}ページ）` : ""}
                        </span>
                    </Stack.Item>
                    <Stack.Item>
                        <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            ariaLabel="引用パネルを閉じる"
                            onClick={onClose}
                            styles={{
                                root: {
                                    color: '#605e5c',
                                },
                                rootHovered: {
                                    backgroundColor: '#e1dfdd',
                                },
                            }}
                        />
                    </Stack.Item>
                </Stack>


                <Stack.Item
                    styles={{
                        root: {
                            padding: '16px',
                            borderBottom: '1px solid #e1e1e1',
                        }
                    }}
                >
                    <h5
                        style={{
                            color: "#0078d4",
                            textDecoration: "underline",
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            wordBreak: 'break-word',
                        }}
                        tabIndex={0}
                        title={activeCitation.url && !activeCitation.url.includes("blob.core") ? activeCitation.url : activeCitation.title ?? ""}
                        onClick={() => onViewSource(activeCitation)}
                    >
                        {activeCitation.title}
                    </h5>
                </Stack.Item>


                <Stack.Item
                    grow
                    styles={{
                        root: {
                            overflow: 'auto',
                            padding: '16px',
                        }
                    }}
                >
                    {
                        activeCitation?.url && urlBlobMap.has(activeCitation.url) &&
                        activeCitation.filepath?.toLowerCase().endsWith('.pdf') ?
                        <PdfViewerWithPagination
                            pdfData={urlBlobMap.get(activeCitation.url!)!}
                            initialPage={activeCitation.page ? Number(activeCitation.page) : 1}
                        />
                        :
                        <div tabIndex={0}>
                            {activeCitation.url && loadingBlobs.has(activeCitation.url) ? (
                                <Stack horizontalAlign="center" styles={{ root: { padding: '20px' } }}>
                                    <Spinner label="読み込み中..." />
                                </Stack>
                            ) : (
                                <ReactMarkdown
                                    linkTarget="_blank"
                                    className={styles.citationPanelContent}
                                    children={activeCitation.content}
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                />
                            )}
                        </div>
                    }
                </Stack.Item>
            </Stack>
        </Panel>
    );
};
